import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { SwiftFlowError, SwiftFlowErrorType } from '@/services/api';

interface DialogEditState {
    open: boolean;
    error: SwiftFlowError | null;
    unifiedMessage: string;
    errorType: SwiftFlowErrorType;
    notify: boolean;
}

interface EditSwiftFlowErrorDialogProps {
    open: boolean;
    editState: DialogEditState | null;
    isSaving: boolean;
    onChange: React.Dispatch<React.SetStateAction<DialogEditState | null>>;
    onSave: () => void;
    onClose: () => void;
}

export function EditSwiftFlowErrorDialog({
    open,
    editState,
    isSaving,
    onChange,
    onSave,
    onClose,
}: EditSwiftFlowErrorDialogProps) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="h6">Edit Swift Flow Error</Typography>
                    {editState?.error && (
                        <Typography variant="body2" color="text.secondary">
                            SwiftFlow Error Code: <b>{editState.error.swiftFlowErrorCode}</b>
                            {editState.error.gateway && <span> · Gateway: <b>{editState.error.gateway}</b></span>}
                        </Typography>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="SwiftFlow Unified Error Message"
                        required
                        multiline
                        fullWidth
                        value={editState?.unifiedMessage ?? ''}
                        onChange={(e) =>
                            onChange((prev) => (prev ? { ...prev, unifiedMessage: e.target.value } : prev))
                        }
                    />
                    <TextField
                        label="Type"
                        select
                        fullWidth
                        value={editState?.errorType ?? 'None'}
                        onChange={(e) =>
                            onChange((prev) => (prev ? { ...prev, errorType: e.target.value } : prev))
                        }
                    >
                        <MenuItem value="Recoverable">Recoverable</MenuItem>
                        <MenuItem value="NonRecoverable">NonRecoverable</MenuItem>
                        <MenuItem value="None">None</MenuItem>
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(editState?.notify)}
                                onChange={(e) =>
                                    onChange((prev) => (prev ? { ...prev, notify: e.target.checked } : prev))
                                }
                            />
                        }
                        label="Notify"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
